import React, { useMemo, useState } from 'react';

import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { AgencyAddItemDialog } from '@components/admin/widgets/AgencyAddItemDialog';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { cellFactory, Table } from '@components/Table/Table';
import { agencyStrings } from '@data/agency';
import { getCityLabelByName } from '@data/cities';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { useUser } from '@hooks/queries/useUser';
import { AgencyListData } from '@models/Agency';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '代理店管理',
    exportSubject: '代理店',
    id: 'NO.',
    details: '詳細',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
}));

interface AgencyRowProps {
  item: AgencyListData;
  checked: boolean;
  index: number;
  onChange: (checked: boolean) => any;
}

export const AgencyRow: React.FC<AgencyRowProps> = (props) => {
  const { item, checked, index, onChange } = props;
  const classes = useCommonStyles();
  const restServiceInfo = item.service_info.slice(1);

  const renderFirstRow = () => (
    <Table.Row
      columns={[
        cellFactory.createCheckable(checked, onChange),
        cellFactory.create(index),
        cellFactory.create(item.name_en_short),
        cellFactory.create(item.fcp_no),
        cellFactory.create(
          <Link
            className={classes.link}
            to={`${url('/admin/widgets/AgencyDetails')}?id=${item.id}`}
          >
            {strings.details}
          </Link>,
        ),
        cellFactory.create(
          `${item.service_info[0]?.service_name || '-'}(${
            item.service_info[0]?.route_number || '-'
          })`,
        ),
        cellFactory.create(
          item.service_info[0]?.cities.map((n) => getCityLabelByName(n)).join('、') || '-',
        ),
        cellFactory.create(item.staff_number),
      ]}
    />
  );

  const renderRestRows = () =>
    restServiceInfo.map((service_info_item, index) => (
      <Table.Row
        key={index}
        columns={[
          cellFactory.createBlank(),
          cellFactory.createBlank(),
          cellFactory.createBlank(),
          cellFactory.createBlank(),
          cellFactory.createBlank(),
          cellFactory.create(
            `${service_info_item.service_name}(${service_info_item.route_number})`,
          ),
          cellFactory.create(service_info_item.cities.map((n) => getCityLabelByName(n)).join('、')),
          cellFactory.createBlank(),
        ]}
      />
    ));
  return (
    <>
      {renderFirstRow()}
      {renderRestRows()}
    </>
  );
};

export const Agencies: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [checklist, setChecklist] = useState<number[]>([]);
  const [exportCsvOpen, setExportCsvOpen] = useState(false);
  const [addItemsOpen, setAddItemsOpen] = useState(false);

  const agencyList = useAgencyList();
  const user = useUser();
  const isAdmin = user.data?.role === 'admin';

  const allChecked = useMemo(
    () =>
      (agencyList.data &&
        agencyList.data?.length > 0 &&
        agencyList.data.map((v) => v.id).every((id) => checklist.includes(id))) ??
      false,
    [checklist, agencyList],
  );

  const checkableHeaderColumn = cellFactory.createCheckable(allChecked, (checked) => {
    if (checked) {
      setChecklist(agencyList.data?.map((item) => item.id) ?? []);
    } else {
      setChecklist([]);
    }
  });

  return (
    <Layout title={strings.title} loading={agencyList.isLoading}>
      <div className={classes.container}>
        <AgencyAddItemDialog open={addItemsOpen} onClose={() => setAddItemsOpen(false)} />
        <ExportCSVDialog
          open={exportCsvOpen}
          onClose={() => setExportCsvOpen(false)}
          subject={strings.exportSubject}
          useMatch={false}
          baseUrl="/dashboard/agency/"
          selectedIds={checklist}
        />
        <SectionTitle
          actions={
            <div className={classes.buttonContainer}>
              {isAdmin && (
                <Button
                  onClick={() => setAddItemsOpen(true)}
                  color="primary"
                  text={formStrings.addNew}
                  shape="round"
                />
              )}
              <Button
                onClick={() => setExportCsvOpen(true)}
                color="primary"
                text={formStrings.exportCsv}
                shape="round"
              />
            </div>
          }
        >
          {strings.title}
        </SectionTitle>
        <Table>
          <Table.Head>
            <Table.Row
              columns={[
                checkableHeaderColumn,
                cellFactory.create(strings.id),
                cellFactory.create(agencyStrings.englishNameShort),
                cellFactory.create(agencyStrings.fcpNo),
                cellFactory.createBlank(),
                cellFactory.create(agencyStrings.service),
                cellFactory.create(agencyStrings.collectCities),
                cellFactory.create(agencyStrings.d2dStaffNumber),
              ]}
            />
          </Table.Head>
          <Table.Body>
            {agencyList.data?.map((item, index) => (
              <AgencyRow
                key={item.id}
                index={index + 1}
                item={item}
                checked={checklist.includes(item.id)}
                onChange={(checked) => {
                  if (checked) {
                    setChecklist([...checklist, item.id]);
                  } else {
                    setChecklist(checklist.filter((id) => id !== item.id));
                  }
                }}
              />
            ))}
          </Table.Body>
        </Table>
      </div>
    </Layout>
  );
};
